import type { Sample, UsageRecord } from '../services/sample.service';
import { USAGE_ACTION } from '../constants/sample.constants';
import { sampleTypeCodeToLegacyJa } from './sampleTypeLabels';
import { buildUsageRecordsSignature } from './sampleUsageRecordSignature';
import type { SampleFormData } from './sampleFormSchema';
import { formatPathogenGenusDisplay } from './pathogenGenusDisplay';

/** 送信時に必要な使用履歴行（フォーム行の最小形） */
export type SampleSubmitUsageRow = {
  id?: number;
  usageDate: string;
  action: string;
  purpose?: string;
  quantity: number;
  remainingQuantity: number;
  recordedBy?: string;
};

export type BuildSampleSubmitPayloadArgs = {
  data: SampleFormData;
  /** 編集時の既存サンプル（新規は falsy） */
  actualInitialData?: Pick<Sample, 'disposalDate'> | null;
  usageRecords: SampleSubmitUsageRow[];
  pathogenOption: 'included' | 'excluded';
  sampleCreatedDate: string;
  calculatedRemainingQuantity: number | undefined;
  initialUsageRecordsSignature: string;
};

export type BuildSampleSubmitPayloadResult = {
  formData: Partial<Sample> & { createdDate?: string };
  usageRecordsChanged: boolean;
};

/**
 * サンプル登録・編集モーダルの API 送信用ペイロードを組み立てる。
 * （disposalDate は送らない／病原体除外時は空／使用履歴は変更時のみ）
 */
export function buildSampleSubmitPayload(
  args: BuildSampleSubmitPayloadArgs
): BuildSampleSubmitPayloadResult {
  const {
    data,
    actualInitialData,
    usageRecords,
    pathogenOption,
    sampleCreatedDate,
    calculatedRemainingQuantity,
    initialUsageRecordsSignature,
  } = args;

  const processedUsageRecords = actualInitialData
    ? usageRecords.map((record) => ({
        id: record.id,
        usageDate: record.usageDate,
        action: record.action,
        purpose:
          record.action === USAGE_ACTION.CREATED ? USAGE_ACTION.CREATED : record.purpose || '',
        quantity: record.action === USAGE_ACTION.CREATED ? 0 : record.quantity,
        remainingQuantity: record.remainingQuantity,
        recordedBy: record.recordedBy || '',
      }))
    : undefined;

  const { disposalDate: _disposalDate, ...dataWithoutDisposalDate } = data;
  void _disposalDate;

  let initialQuantityValue: number | undefined;
  if (actualInitialData) {
    const creationRecord = usageRecords.find((record) => record.action === USAGE_ACTION.CREATED);
    initialQuantityValue = creationRecord?.remainingQuantity ?? undefined;
  } else {
    initialQuantityValue =
      data.initialQuantity !== null && data.initialQuantity !== undefined
        ? Number(data.initialQuantity)
        : undefined;
  }

  const usageRecordsChanged = Boolean(
    actualInitialData &&
      !actualInitialData.disposalDate &&
      buildUsageRecordsSignature(usageRecords) !== initialUsageRecordsSignature
  );

  const pathogenExcluded = pathogenOption === 'excluded';

  const formData: Partial<Sample> & { createdDate?: string } = {
    ...dataWithoutDisposalDate,
    sampleType: sampleTypeCodeToLegacyJa(data.sampleType) ?? data.sampleType,
    initialQuantity: initialQuantityValue,
    remainingQuantity: actualInitialData ? calculatedRemainingQuantity : initialQuantityValue,
    unit: data.unit?.trim() || undefined,
    storageDate: data.storageDate || undefined,
    // API（Zod string.optional）は null を拒否するため、空は undefined で省略する
    notes: data.notes?.trim() ? data.notes : undefined,
    drawerNote: data.drawerNote?.trim() ? data.drawerNote : undefined,
    storageRoomId: data.storageRoomId || undefined,
    drawerNumber: data.drawerNumber ?? undefined,
    pathogenGenus: pathogenExcluded
      ? undefined
      : formatPathogenGenusDisplay(data.pathogenGenus) || undefined,
    pathogenSpecies: pathogenExcluded ? undefined : data.pathogenSpecies || undefined,
    pathogenSubspecies: pathogenExcluded ? undefined : data.pathogenSubspecies || undefined,
    pathogenStrain: pathogenExcluded ? undefined : data.pathogenStrain || undefined,
    pathogenNote: pathogenExcluded
      ? undefined
      : data.pathogenNote?.trim()
        ? data.pathogenNote
        : undefined,
    ...(!actualInitialData && sampleCreatedDate ? { createdDate: sampleCreatedDate } : {}),
    ...(usageRecordsChanged && processedUsageRecords && processedUsageRecords.length > 0
      ? { usageRecords: processedUsageRecords as UsageRecord[] }
      : {}),
  };

  return { formData, usageRecordsChanged };
}
